import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowLeft, ArrowRight, Download, Moon, Sun, X } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { AREAS, AREA_NAMES_EN, areaBand, areaBandEn, globalBand, globalBandEn } from "@/questions";
import { downloadCampaignPdf } from "@/reportDownload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STR = {
  it: {
    eyebrow: "Report — Questionario di Benessere Aziendale",
    overview: "Panoramica",
    areasTitle: "Le 13 aree — dalla più critica alla migliore",
    deptsTitle: "Dettaglio per reparto",
    generated: "Report generato il",
    completed: "Questionari completati",
    completion: "di completamento",
    globalScore: "Punteggio globale medio",
    priorityAreas: "Aree con media inferiore a 15/25",
    noData: "Nessun dato",
    noResponses: "Nessuna risposta raccolta al momento.",
    responses: "risposte",
    downloadPdf: "Scarica PDF",
    exit: "Esci",
    slideOf: "di",
    hint: "Usa ← → per navigare · Esc per uscire",
    scoreOf: "/25",
  },
  en: {
    eyebrow: "Report — Corporate Wellbeing Questionnaire",
    overview: "Overview",
    areasTitle: "The 13 areas — from most critical to best",
    deptsTitle: "Breakdown by department",
    generated: "Report generated on",
    completed: "Completed questionnaires",
    completion: "completion",
    globalScore: "Average global score",
    priorityAreas: "Areas averaging below 15/25",
    noData: "No data",
    noResponses: "No responses collected yet.",
    responses: "responses",
    downloadPdf: "Download PDF",
    exit: "Exit",
    slideOf: "of",
    hint: "Use ← → to navigate · Esc to exit",
    scoreOf: "/25",
  },
};

const THEMES = {
  dark: {
    page: "bg-navy-950 text-cream",
    sub: "text-cream/55",
    faint: "text-cream/35",
    line: "border-cream/10",
    panel: "bg-white/[0.04] border-cream/10",
    track: "bg-cream/10",
    barOk: "#C5A880",
    barPriority: "#A38053",
    tick: "#FAF9F6",
    chip: "border-gold/40 bg-gold/10 text-gold-light",
    btn: "border-cream/20 text-cream hover:bg-cream hover:text-navy-950",
    dotActive: "bg-gold",
    dot: "bg-cream/25",
  },
  light: {
    page: "bg-cream text-navy-900",
    sub: "text-navy-900/55",
    faint: "text-navy-900/35",
    line: "border-navy-900/10",
    panel: "bg-white border-navy-900/10",
    track: "bg-navy-900/8",
    barOk: "#3C5A4B",
    barPriority: "#A38053",
    tick: "#0D1B2A",
    chip: "border-gold-deep/60 bg-gold-light/40 text-navy-900",
    btn: "border-navy-900/25 text-navy-900 hover:bg-navy-900 hover:text-cream",
    dotActive: "bg-gold-deep",
    dot: "bg-navy-900/20",
  },
};

const ChartTooltip = ({ active, payload, theme, bandFn }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className={`border px-4 py-3 text-sm shadow-xl ${theme.panel} ${theme.page.includes("navy-950") ? "text-cream" : "text-navy-900"}`} data-testid="chart-tooltip">
      <p className="font-medium">{d.name}</p>
      <p className="mt-1 font-mono text-xs opacity-70">
        {d.score > 0 ? `${d.score.toFixed(1)} / 25 · ${bandFn(Math.round(d.score))}` : "—"}
      </p>
    </div>
  );
};

export default function Presentation() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem("eclenia-admin-token"));
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [slide, setSlide] = useState(0);
  const [theme, setTheme] = useState("dark");
  const [lang, setLang] = useState("it");
  const [selectedDept, setSelectedDept] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const t = STR[lang];
  const th = THEMES[theme];
  const bandFn = lang === "it" ? areaBand : areaBandEn;
  const globalBandFn = lang === "it" ? globalBand : globalBandEn;
  const areaNames = lang === "it" ? AREAS.map((a) => a.name) : AREA_NAMES_EN;

  useEffect(() => {
    document.title = "Presentazione — ECLENIA";
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "robots";
      document.head.appendChild(m);
    }
    m.content = "noindex, nofollow";
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/admin", { replace: true });
      return;
    }
    axios
      .get(`${API}/admin/campaigns/${campaignId}/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setStats(data);
        if (data.departments?.length) setSelectedDept(data.departments[0].name);
      })
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("eclenia-admin-token");
          navigate("/admin", { replace: true });
        } else {
          setError(true);
        }
      });
  }, [token, campaignId, navigate]);

  const slides = useMemo(() => {
    if (!stats) return [];
    const s = ["overview", "areas"];
    if (stats.departments?.length) s.push("departments");
    return s;
  }, [stats]);

  const go = useCallback(
    (dir) => setSlide((s) => Math.min(slides.length - 1, Math.max(0, s + dir))),
    [slides.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "Escape") navigate("/admin");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, navigate]);

  if (!token) return null;

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadCampaignPdf(token, campaignId, lang, stats?.campaign?.name);
    } finally {
      setPdfLoading(false);
    }
  };

  const renderSlide = () => {
    const c = stats.campaign;
    const pct = stats.target > 0 ? Math.min(100, Math.round((stats.completed / stats.target) * 100)) : 0;
    const key = slides[slide];

    if (key === "overview") {
      const priority = stats.area_avgs.filter((a) => a < 15 && a > 0).length;
      return (
        <div className="mx-auto flex h-full w-full max-w-6xl flex-col justify-center px-6 pt-10 sm:px-12" data-testid="slide-overview">
          <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold-deep">
            <span className="inline-block h-px w-14 bg-gold-deep" /> {t.eyebrow}
          </p>
          <h1 className="mt-8 font-serif text-5xl tracking-tight sm:text-6xl lg:text-7xl" data-testid="overview-company">{c.name}</h1>
          <p className={`mt-4 text-sm ${th.sub}`}>
            {t.generated} {new Date().toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { day: "numeric", month: "long", year: "numeric" })} · {t.completed}: {stats.completed}/{stats.target} ({pct}%)
          </p>
          <div className={`mt-14 grid gap-px border ${th.line} sm:grid-cols-3`}>
            <div className={`border p-8 ${th.panel}`} data-testid="overview-completion">
              <p className="font-mono text-4xl">{stats.completed}<span className={`text-xl ${th.faint}`}>/{stats.target}</span></p>
              <p className={`mt-3 text-sm font-medium ${th.sub}`}>{t.completed}</p>
              <div className={`mt-4 h-1.5 w-full ${th.track}`}>
                <motion.div className="h-1.5 bg-gold" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, delay: 0.3 }} />
              </div>
              <p className={`mt-2 text-xs ${th.faint}`}>{pct}% {t.completion}</p>
            </div>
            <div className={`border p-8 ${th.panel}`} data-testid="overview-global">
              <p className="font-mono text-4xl">
                {stats.completed ? stats.avg_global : "—"}<span className={`text-xl ${th.faint}`}>/325</span>
              </p>
              <p className={`mt-3 text-sm font-medium ${th.sub}`}>{t.globalScore}</p>
              <p className={`mt-3 inline-block border px-3 py-1 text-[11px] uppercase tracking-[0.15em] ${th.chip}`} data-testid="overview-global-band">
                {stats.completed ? globalBandFn(Math.round(stats.avg_global)) : t.noData}
              </p>
            </div>
            <div className={`border p-8 ${th.panel}`} data-testid="overview-priority">
              <p className="font-mono text-4xl">{stats.completed ? priority : "—"}<span className={`text-xl ${th.faint}`}>/13</span></p>
              <p className={`mt-3 text-sm font-medium ${th.sub}`}>{t.priorityAreas}</p>
              {!stats.completed && <p className={`mt-3 text-xs italic ${th.faint}`} data-testid="overview-no-responses">{t.noResponses}</p>}
            </div>
          </div>
        </div>
      );
    }

    if (key === "areas") {
      const data = stats.area_avgs
        .map((avg, i) => ({ name: areaNames[i], score: avg }))
        .sort((a, b) => a.score - b.score);
      return (
        <div className="mx-auto flex h-full w-full max-w-6xl flex-col justify-center px-6 pt-10 sm:px-12" data-testid="slide-areas">
          <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold-deep">
            <span className="inline-block h-px w-14 bg-gold-deep" /> {t.areasTitle}
          </p>
          <div className="mt-8 w-full" style={{ height: 13 * 40 }} data-testid="areas-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ top: 0, right: 60, bottom: 0, left: 10 }} barCategoryGap="28%">
                <XAxis type="number" domain={[0, 25]} hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={250}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: th.tick, fontSize: 12.5, fillOpacity: 0.85 }}
                />
                <Tooltip cursor={{ fill: theme === "dark" ? "rgba(250,249,246,0.05)" : "rgba(13,27,42,0.04)" }} content={<ChartTooltip theme={th} bandFn={bandFn} />} />
                <Bar dataKey="score" radius={0} isAnimationActive animationDuration={800}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.score > 0 && d.score < 15 ? th.barPriority : th.barOk} />
                  ))}
                  <LabelList
                    dataKey="score"
                    position="right"
                    formatter={(v) => (v > 0 ? `${v.toFixed(1)} · ${bandFn(Math.round(v))}` : "—")}
                    style={{ fill: th.tick, fillOpacity: 0.6, fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className={`mt-4 text-xs ${th.faint}`}>
            {lang === "it" ? "Scala 0–25" : "Scale 0–25"}{stats.completed ? "" : ` — ${t.noResponses}`}
          </p>
        </div>
      );
    }

    // departments
    const dept = stats.departments.find((d) => d.name === selectedDept) || stats.departments[0];
    return (
      <div className="mx-auto flex h-full w-full max-w-6xl flex-col justify-center px-6 pt-10 sm:px-12" data-testid="slide-departments">
        <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold-deep">
          <span className="inline-block h-px w-14 bg-gold-deep" /> {t.deptsTitle}
        </p>
        <div className="mt-8 flex flex-wrap gap-3" data-testid="dept-cards">
          {stats.departments.map((d) => (
            <button
              key={d.name}
              onClick={() => setSelectedDept(d.name)}
              data-testid={`dept-card-${d.name}`}
              className={`border px-5 py-4 text-left transition-colors duration-300 ${
                dept.name === d.name ? `${th.panel} border-gold-deep/60` : `${th.line} ${th.sub} hover:border-gold-deep/40`
              }`}
            >
              <p className="text-sm font-medium">{d.name}</p>
              <p className={`mt-1 font-mono text-xs ${th.faint}`}>
                {d.completed} {t.responses} · {d.completed ? `${d.avg_global}/325` : t.noData}
              </p>
              {d.completed > 0 && (
                <p className={`mt-2 inline-block border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${th.chip}`}>
                  {globalBandFn(Math.round(d.avg_global))}
                </p>
              )}
            </button>
          ))}
        </div>
        <div className="mt-10 grid gap-x-14 gap-y-4 sm:grid-cols-2" data-testid="dept-areas">
          {areaNames.map((name, i) => {
            const avg = dept.area_avgs[i] || 0;
            const priority = dept.completed > 0 && avg > 0 && avg < 15;
            return (
              <div key={name} data-testid={`dept-area-${i}`} title={dept.completed ? `${avg.toFixed(1)}/25 · ${bandFn(Math.round(avg))}` : t.noData}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className={`text-[13px] ${th.sub}`}>{name}</p>
                  <p className={`font-mono text-xs ${th.faint}`}>
                    {dept.completed ? avg.toFixed(1) : "—"} <span className="opacity-70">· {dept.completed ? bandFn(Math.round(avg)) : ""}</span>
                  </p>
                </div>
                <div className={`mt-1.5 h-1.5 w-full ${th.track}`}>
                  <motion.div
                    className="h-1.5"
                    style={{ backgroundColor: priority ? th.barPriority : th.barOk }}
                    initial={{ width: 0 }}
                    animate={{ width: `${(avg / 25) * 100}%` }}
                    transition={{ duration: 0.7, delay: i * 0.04 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${th.page} transition-colors duration-500`} data-testid="presentation-root">
      {/* Top bar */}
      <header className={`flex items-center justify-between border-b px-5 py-3.5 sm:px-8 ${th.line}`}>
        <div className="flex items-center gap-3">
          <LogoMark theme={theme === "dark" ? "dark" : "light"} size={26} />
          <div>
            <p className="font-serif text-base leading-tight tracking-tight">ECLENIA</p>
            <p className={`text-[9px] uppercase tracking-[0.26em] ${th.faint}`} data-testid="presentation-company">{stats?.campaign?.name || ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center border text-[10px] font-medium tracking-widest ${th.line}`} data-testid="presentation-lang-toggle">
            {["it", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`presentation-lang-${l}`}
                className={`px-2.5 py-1.5 uppercase transition-colors ${lang === l ? (theme === "dark" ? "bg-cream text-navy-950" : "bg-navy-900 text-cream") : th.sub}`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className={`border p-2 transition-colors ${th.btn}`}
            data-testid="presentation-theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button
            onClick={downloadPdf}
            disabled={pdfLoading || !stats}
            className={`flex items-center gap-2 border px-3.5 py-2 text-[10px] font-medium uppercase tracking-[0.16em] transition-colors disabled:opacity-50 ${th.btn}`}
            data-testid="presentation-pdf-btn"
          >
            <Download size={13} /> {pdfLoading ? "…" : t.downloadPdf}
          </button>
          <button
            onClick={() => navigate("/admin")}
            className={`border p-2 transition-colors ${th.btn}`}
            data-testid="presentation-exit-btn"
            aria-label={t.exit}
          >
            <X size={14} />
          </button>
        </div>
      </header>

      {/* Slide area */}
      <main className="relative flex-1 overflow-y-auto overflow-x-hidden">
        {!stats && !error && (
          <div className={`flex h-full items-center justify-center text-sm ${th.sub}`} data-testid="presentation-loading">…</div>
        )}
        {error && (
          <div className={`flex h-full items-center justify-center text-sm ${th.sub}`} data-testid="presentation-error">
            {lang === "it" ? "Campagna non trovata." : "Campaign not found."}
          </div>
        )}
        {stats && (
          <AnimatePresence mode="wait">
            <motion.div
              key={slides[slide] + lang}
              className="h-full"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              {renderSlide()}
            </motion.div>
          </AnimatePresence>
        )}
      </main>

      {/* Bottom nav */}
      <footer className={`flex items-center justify-between border-t px-5 py-3.5 sm:px-8 ${th.line}`}>
        <p className={`hidden text-[10px] uppercase tracking-[0.18em] sm:block ${th.faint}`}>{t.hint}</p>
        <div className="flex items-center gap-5">
          <button
            onClick={() => go(-1)}
            disabled={slide === 0}
            className={`border p-2 transition-colors disabled:opacity-30 ${th.btn}`}
            data-testid="presentation-prev-btn"
            aria-label="Previous"
          >
            <ArrowLeft size={14} />
          </button>
          <div className="flex items-center gap-2" data-testid="presentation-dots">
            {slides.map((s, i) => (
              <button
                key={s}
                onClick={() => setSlide(i)}
                data-testid={`presentation-dot-${s}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? `w-7 ${th.dotActive}` : `w-1.5 ${th.dot}`}`}
                aria-label={s}
              />
            ))}
          </div>
          <p className={`font-mono text-[11px] ${th.faint}`} data-testid="presentation-counter">
            {String(slide + 1).padStart(2, "0")} {t.slideOf} {String(slides.length).padStart(2, "0")}
          </p>
          <button
            onClick={() => go(1)}
            disabled={slide >= slides.length - 1}
            className={`border p-2 transition-colors disabled:opacity-30 ${th.btn}`}
            data-testid="presentation-next-btn"
            aria-label="Next"
          >
            <ArrowRight size={14} />
          </button>
        </div>
        <span className="hidden sm:block sm:w-40" />
      </footer>
    </div>
  );
}
